import React, { useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, X, ArrowRight, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Shared Cash App QR payment modal.
 *
 * Props:
 *   open, onClose, plan {id,name,amount}, cashTag, user, redirectAfter, onSubmitted
 */
export default function CashAppModal({
  open,
  onClose,
  plan,
  cashTag,
  user,
  redirectAfter,
  onSubmitted,
}) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  if (!open || !plan) return null;

  const tag = cashTag || "$DimekaH";
  const cashLink = `https://cash.app/${tag.replace(/^\$/, "$")}/${plan.amount}`;

  const copyTag = async () => {
    try {
      await navigator.clipboard.writeText(tag);
      toast.success("Cashtag copied.");
    } catch {
      toast.error("Couldn't copy — long-press to select the tag.");
    }
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/payments/cash-app-request", {
        plan: plan.id,
        cashtag_note: note,
      });
      toast.success("Payment logged. An instructor will verify and unlock your access.");
      setNote("");
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="cashapp-modal"
    >
      <div
        className="bg-card border border-border rounded-lg max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
          data-testid="cashapp-modal-close"
        >
          <X size={18} />
        </button>
        <div className="label-caps mb-2 text-primary">Cash App · {plan.name}</div>
        <h3 className="font-serif text-3xl mb-4">
          Pay ${plan.amount.toFixed(0)} to {tag}
        </h3>
        <div className="flex items-center justify-center mb-5" data-testid="cashapp-qr">
          <div className="bg-white p-3 rounded-md border border-border">
            <QRCodeSVG
              value={cashLink}
              size={168}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              imageSettings={{
                src: "https://cash.app/favicon.ico",
                height: 28,
                width: 28,
                excavate: true,
              }}
            />
          </div>
        </div>
        <div className="text-center text-xs text-muted-foreground mb-5">
          📱 <strong>On your phone?</strong> Scan the QR — it opens Cash App with{" "}
          <span className="text-foreground">{tag}</span> pre-filled.
        </div>
        <ol className="space-y-2 text-sm mb-5">
          <li>
            <strong>1.</strong> Send <strong>${plan.amount.toFixed(0)}</strong> to <strong>{tag}</strong>.
          </li>
          <li>
            <strong>2.</strong> Include your email
            {user?.email ? (<> (<em>{user.email}</em>)</>) : (<> (the one you'll register with)</>)}{" "}
            in the Cash App note so we can match the payment.
          </li>
          <li>
            <strong>3.</strong> Tap the button below — an instructor will verify and unlock your access.
          </li>
        </ol>
        <div className="flex flex-wrap gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={copyTag} data-testid="cashapp-copy-handle">
            <Copy size={14} className="mr-1.5" /> Copy Cashtag
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="cashapp-external-link">
            <a href={cashLink} target="_blank" rel="noreferrer">
              <Wallet size={14} className="mr-1.5" /> Open Cash App
            </a>
          </Button>
        </div>
        <Input
          placeholder="Optional note (e.g., 'Sent from $YourTag @ 3:12 PM')"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mb-3"
          data-testid="cashapp-note-input"
        />
        {redirectAfter ? (
          <Button asChild className="w-full" data-testid="cashapp-onboard-link">
            <Link to={user ? redirectAfter : `/register?next=${encodeURIComponent(redirectAfter)}`}>
              I've paid — continue <ArrowRight size={14} className="ml-2" />
            </Link>
          </Button>
        ) : (
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={onClose} data-testid="cashapp-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={sending} data-testid="cashapp-submit-btn">
              {sending ? "Submitting…" : "I've paid — verify me"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
